import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, Globe, Instagram, Linkedin, Users, Target, Heart, Sparkles, MapPin, Calendar, MessageSquare, FileText, ChevronRight, ExternalLink } from 'lucide-react';
import { MatchStrength } from './MatchStrength';
interface ProfilePresentationProps {
  isOpen: boolean;
  onClose: () => void;
  profile: any;
  role: 'brand' | 'organizer';
  viewerRole: 'brand' | 'organizer';
  matchPercentage?: number;
  onMessage?: () => void;
  onSchedule?: () => void;
  onContract?: () => void;
}
export function ProfilePresentation({
  isOpen,
  onClose,
  profile,
  role,
  viewerRole,
  matchPercentage = 92,
  onMessage,
  onSchedule,
  onContract
}: ProfilePresentationProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const accentColor = role === 'brand' ? 'cyan' : 'purple';
  const accentBg = role === 'brand' ? 'bg-cyan-500' : 'bg-purple-500';
  const accentBgLight = role === 'brand' ? 'bg-cyan-500/10' : 'bg-purple-500/10';
  const accentText = role === 'brand' ? 'text-cyan-400' : 'text-purple-400';
  const accentBorder = role === 'brand' ? 'border-cyan-500' : 'border-purple-500';
  // Mock data for presentation
  const slides = [{
    id: 'intro',
    title: 'Introduction'
  }, {
    id: 'story',
    title: 'Our Story'
  }, {
    id: 'audience',
    title: 'Audience'
  }, {
    id: 'goals',
    title: 'Goals & Values'
  }, {
    id: 'gallery',
    title: 'Gallery'
  }, {
    id: 'connect',
    title: 'Connect'
  }];
  const nextSlide = () => setCurrentSlide(prev => Math.min(prev + 1, slides.length - 1));
  const prevSlide = () => setCurrentSlide(prev => Math.max(prev - 1, 0));
  return <AnimatePresence data-id="element-497">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-slate-950 z-50 overflow-hidden" data-id="element-498">
          {/* Header */}
          <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-slate-950 via-slate-950/80 to-transparent" data-id="element-499">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-500">
              <div className="flex items-center gap-4" data-id="element-501">
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-502">
                  <X size={24} data-id="element-503" />
                </button>
                <div className="flex items-center gap-3" data-id="element-504">
                  <div className={`w-10 h-10 rounded-lg ${accentBgLight} flex items-center justify-center font-bold ${accentText}`} data-id="element-505">
                    {profile?.name?.[0] || 'P'}
                  </div>
                  <div data-id="element-506">
                    <h1 className="text-lg font-bold text-white" data-id="element-507">
                      {profile?.name || 'Profile'}
                    </h1>
                    <p className="text-sm text-slate-400" data-id="element-508">
                      {role === 'brand' ? 'Brand Profile' : 'Event Profile'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4" data-id="element-509">
                <MatchStrength percentage={matchPercentage} data-id="element-510" />
              </div>
            </div>
          </div>

          {/* Slide Navigation Dots */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2" data-id="element-511">
            {slides.map((slide, i) => <button key={slide.id} onClick={() => setCurrentSlide(i)} className={`h-2 rounded-full transition-all ${i === currentSlide ? `w-8 ${accentBg}` : 'w-2 bg-slate-700 hover:bg-slate-600'}`} data-id="element-512" />)}
          </div>

          {/* Navigation Arrows */}
          <button onClick={prevSlide} disabled={currentSlide === 0} className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-slate-800/80 text-white hover:bg-slate-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-513">
            <ArrowLeft size={24} data-id="element-514" />
          </button>
          <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-slate-800/80 text-white hover:bg-slate-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-515">
            <ArrowRight size={24} data-id="element-516" />
          </button>

          {/* Slides */}
          <div className="h-full pt-20 pb-20 overflow-hidden" data-id="element-517">
            <AnimatePresence mode="wait" data-id="element-518">
              {/* Slide 0: Introduction */}
              {currentSlide === 0 && <motion.div key="intro" initial={{
            opacity: 0,
            x: 100
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -100
          }} className="h-full flex items-center justify-center px-8" data-id="element-519">
                  <div className="max-w-4xl mx-auto text-center" data-id="element-520">
                    <motion.div initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                delay: 0.2
              }} className={`w-32 h-32 rounded-3xl ${accentBgLight} flex items-center justify-center mx-auto mb-8 text-5xl font-bold ${accentText}`} data-id="element-521">
                      {profile?.logo ? <img src={profile.logo} alt="" className="w-full h-full object-cover rounded-3xl" data-id="element-522" /> : profile?.name?.[0] || 'P'}
                    </motion.div>

                    <motion.h1 initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.3
              }} className="text-5xl md:text-7xl font-bold text-white mb-6" data-id="element-523">
                      {profile?.name || 'Company Name'}
                    </motion.h1>

                    <motion.p initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.4
              }} className="text-2xl text-slate-400 mb-8" data-id="element-524">
                      {profile?.tagline || 'Your tagline goes here'}
                    </motion.p>

                    <motion.div initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.5
              }} className="flex items-center justify-center gap-4" data-id="element-525">
                      {profile?.website && <a href={profile.website} target="_blank" rel="noopener noreferrer" className="p-3 rounded-full bg-slate-800 text-slate-400 hover:text-white transition-colors" data-id="element-526">
                          <Globe size={20} data-id="element-527" />
                        </a>}
                      {profile?.instagram && <a href={`https://instagram.com/${profile.instagram}`} target="_blank" rel="noopener noreferrer" className="p-3 rounded-full bg-slate-800 text-slate-400 hover:text-white transition-colors" data-id="element-528">
                          <Instagram size={20} data-id="element-529" />
                        </a>}
                      {profile?.linkedin && <a href={profile.linkedin} target="_blank" rel="noopener noreferrer" className="p-3 rounded-full bg-slate-800 text-slate-400 hover:text-white transition-colors" data-id="element-530">
                          <Linkedin size={20} data-id="element-531" />
                        </a>}
                    </motion.div>
                  </div>
                </motion.div>}

              {/* Slide 1: Story */}
              {currentSlide === 1 && <motion.div key="story" initial={{
            opacity: 0,
            x: 100
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -100
          }} className="h-full flex items-center justify-center px-8" data-id="element-532">
                  <div className="max-w-4xl mx-auto" data-id="element-533">
                    <motion.div initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${accentBgLight} ${accentText} text-sm font-medium mb-8`} data-id="element-534">
                      <Sparkles size={16} data-id="element-535" /> Our Story
                    </motion.div>

                    <motion.h2 initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.1
              }} className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-536">
                      {role === 'brand' ? 'What We Do' : 'About the Event'}
                    </motion.h2>

                    <motion.p initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.2
              }} className="text-xl text-slate-300 leading-relaxed mb-8" data-id="element-537">
                      {profile?.description || 'A compelling description of what this company or event is all about. This is where the story comes to life and potential partners can understand the vision and mission.'}
                    </motion.p>

                    <motion.div initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.3
              }} className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6" data-id="element-538">
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-539">
                        Why We Started
                      </h3>
                      <p className="text-slate-400" data-id="element-540">
                        {profile?.origin || profile?.mission || 'The origin story and mission that drives everything we do.'}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>}

              {/* Slide 2: Audience */}
              {currentSlide === 2 && <motion.div key="audience" initial={{
            opacity: 0,
            x: 100
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -100
          }} className="h-full flex items-center justify-center px-8" data-id="element-541">
                  <div className="max-w-5xl mx-auto w-full" data-id="element-542">
                    <motion.div initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${accentBgLight} ${accentText} text-sm font-medium mb-8`} data-id="element-543">
                      <Users size={16} data-id="element-544" />{' '}
                      {role === 'brand' ? 'Target Audience' : 'Who Attends'}
                    </motion.div>

                    <div className="grid md:grid-cols-3 gap-6" data-id="element-545">
                      <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.1
                }} className="bg-slate-800 border border-slate-700 rounded-2xl p-6 text-center" data-id="element-546">
                        <div className={`text-5xl font-bold ${accentText} mb-2`} data-id="element-547">
                          {profile?.ageMin || 18}-{profile?.ageMax || 45}
                        </div>
                        <p className="text-slate-400" data-id="element-548">Age Range</p>
                      </motion.div>

                      <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.2
                }} className="bg-slate-800 border border-slate-700 rounded-2xl p-6 text-center" data-id="element-549">
                        <div className={`text-5xl font-bold ${accentText} mb-2`} data-id="element-550">
                          {profile?.audienceSize?.toLocaleString() || '10K+'}
                        </div>
                        <p className="text-slate-400" data-id="element-551">
                          {role === 'brand' ? 'Reach' : 'Attendees'}
                        </p>
                      </motion.div>

                      <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.3
                }} className="bg-slate-800 border border-slate-700 rounded-2xl p-6 text-center" data-id="element-552">
                        <div className={`text-5xl font-bold ${accentText} mb-2`} data-id="element-553">
                          {profile?.geoReach || 'National'}
                        </div>
                        <p className="text-slate-400" data-id="element-554">Geographic Reach</p>
                      </motion.div>
                    </div>

                    <motion.div initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.4
              }} className="mt-8" data-id="element-555">
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4" data-id="element-556">
                        Interests
                      </h3>
                      <div className="flex flex-wrap gap-3" data-id="element-557">
                        {(profile?.interests || ['Tech', 'Innovation', 'Sustainability', 'Business']).map((interest: string, i: number) => <span key={i} className={`px-4 py-2 rounded-full ${accentBgLight} ${accentText} text-sm font-medium`} data-id="element-558">
                            {interest}
                          </span>)}
                      </div>
                    </motion.div>
                  </div>
                </motion.div>}

              {/* Slide 3: Goals & Values */}
              {currentSlide === 3 && <motion.div key="goals" initial={{
            opacity: 0,
            x: 100
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -100
          }} className="h-full flex items-center justify-center px-8" data-id="element-559">
                  <div className="max-w-5xl mx-auto w-full" data-id="element-560">
                    <motion.div initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${accentBgLight} ${accentText} text-sm font-medium mb-8`} data-id="element-561">
                      <Target size={16} data-id="element-562" /> Goals & Values
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8" data-id="element-563">
                      <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.1
                }} data-id="element-564">
                        <h3 className="text-2xl font-bold text-white mb-4" data-id="element-565">
                          {role === 'brand' ? 'Primary Goal' : 'What We Offer'}
                        </h3>
                        <div className={`bg-slate-800 border-l-4 ${accentBorder} rounded-r-xl p-6`} data-id="element-566">
                          <p className="text-xl text-white font-medium" data-id="element-567">
                            {profile?.primaryGoal || 'Brand Awareness'}
                          </p>
                          <p className="text-slate-400 mt-2" data-id="element-568">
                            {profile?.successDefinition || 'Creating meaningful connections and lasting partnerships.'}
                          </p>
                        </div>
                      </motion.div>

                      <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.2
                }} data-id="element-569">
                        <h3 className="text-2xl font-bold text-white mb-4" data-id="element-570">
                          Values We Stand For
                        </h3>
                        <div className="space-y-3" data-id="element-571">
                          {(profile?.values || ['Innovation', 'Sustainability', 'Community']).map((value: string, i: number) => <div key={i} className="flex items-center gap-3 bg-slate-800 rounded-xl p-4" data-id="element-572">
                              <Heart size={20} className={accentText} data-id="element-573" />
                              <span className="text-white font-medium" data-id="element-574">
                                {value}
                              </span>
                            </div>)}
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </motion.div>}

              {/* Slide 4: Gallery */}
              {currentSlide === 4 && <motion.div key="gallery" initial={{
            opacity: 0,
            x: 100
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -100
          }} className="h-full flex items-center justify-center px-8" data-id="element-575">
                  <div className="max-w-6xl mx-auto w-full" data-id="element-576">
                    <motion.div initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} className="text-center mb-8" data-id="element-577">
                      <h2 className="text-4xl font-bold text-white mb-4" data-id="element-578">
                        Gallery
                      </h2>
                      <p className="text-slate-400" data-id="element-579">
                        Visual highlights and media
                      </p>
                    </motion.div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-580">
                      {[1, 2, 3, 4, 5, 6].map(i => <motion.div key={i} initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  delay: i * 0.1
                }} className="aspect-video bg-slate-800 rounded-xl overflow-hidden" data-id="element-581">
                          {profile?.images?.[i - 1] ? <img src={profile.images[i - 1]} alt="" className="w-full h-full object-cover" data-id="element-582" /> : <div className="w-full h-full flex items-center justify-center text-slate-600" data-id="element-583">
                              <span className="text-sm" data-id="element-584">Image {i}</span>
                            </div>}
                        </motion.div>)}
                    </div>
                  </div>
                </motion.div>}

              {/* Slide 5: Connect */}
              {currentSlide === 5 && <motion.div key="connect" initial={{
            opacity: 0,
            x: 100
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -100
          }} className="h-full flex items-center justify-center px-8" data-id="element-585">
                  <div className="max-w-2xl mx-auto text-center" data-id="element-586">
                    <motion.div initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} className={`w-24 h-24 rounded-full ${accentBgLight} flex items-center justify-center mx-auto mb-8`} data-id="element-587">
                      <Sparkles size={40} className={accentText} data-id="element-588" />
                    </motion.div>

                    <motion.h2 initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.1
              }} className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-589">
                      Let's Work Together
                    </motion.h2>

                    <motion.p initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.2
              }} className="text-xl text-slate-400 mb-10" data-id="element-590">
                      {profile?.dreamMatch || "We're looking for partners who share our vision and values. Ready to create something amazing?"}
                    </motion.p>

                    <motion.div initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.3
              }} className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-591">
                      <button onClick={onMessage} className={`px-8 py-4 rounded-full font-bold text-lg transition-all flex items-center justify-center gap-2 ${role === 'brand' ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900' : 'bg-purple-600 hover:bg-purple-500 text-white'}`} data-id="element-592">
                        <MessageSquare size={20} data-id="element-593" /> Send Message
                      </button>
                      <button onClick={onSchedule} className="px-8 py-4 rounded-full font-bold text-lg border border-slate-600 hover:border-slate-400 text-white transition-colors flex items-center justify-center gap-2" data-id="element-594">
                        <Calendar size={20} data-id="element-595" /> Schedule Call
                      </button>
                    </motion.div>

                    <motion.button initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.4
              }} onClick={onContract} className="mt-6 text-slate-400 hover:text-white transition-colors flex items-center gap-2 mx-auto" data-id="element-596">
                      <FileText size={18} data-id="element-597" /> Create Contract{' '}
                      <ChevronRight size={18} data-id="element-598" />
                    </motion.button>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>}
    </AnimatePresence>;
}